<template>
    <div class="wiz-page">
        <!-- 面包屑 -->
        <div class="crumb"><span>人群运营</span> / <span>运营任务</span> / <b>{{ isEditing ? '编辑任务' : '新建任务' }}</b></div>

        <!-- 步骤条 -->
        <div class="stepper">
            <template v-for="(s, i) in STEPS" :key="i">
                <div class="step">
                    <div class="step-dot">{{ i + 1 }}</div>
                    <div class="step-meta">
                        <div class="step-name">{{ s.name }}</div>
                        <div class="step-desc">{{ s.desc }}</div>
                    </div>
                </div>
                <div v-if="i < STEPS.length - 1" class="step-line"></div>
            </template>
        </div>

        <!-- 未完成校验提示 -->
        <div class="val-summary" :class="{ active: issues.length }">
            <div class="val-title">⚠ 以下配置尚未完成，请补充后再创建</div>
            <ul class="val-list"><li v-for="(it, i) in issues" :key="i">{{ it }}</li></ul>
        </div>

        <!-- 第 1 步：目标人群 -->
        <section class="section" id="section1">
            <div class="section-head"><span class="section-num">1</span>选择目标人群</div>
            <div class="section-tip">可多选人群，系统将对其并集客户执行后续运营动作。已选人群的重合客户自动去重。人群较多时可用分类标签与搜索快速定位。</div>
            <div class="sel-bar">
                <div class="sel-bar-label">已选 <b>{{ selectedAuds.length }}</b> 个人群</div>
                <div class="chips">
                    <span v-for="a in selectedAuds" :key="a.id" class="chip">{{ a.name }} <b class="chip-x" @click="toggleAud(a)">×</b></span>
                    <span v-if="!selectedAuds.length" class="chips-empty">尚未选择人群</span>
                </div>
            </div>
            <div class="aud-toolbar">
                <input class="field-input aud-search" v-model="audSearch" placeholder="搜索人群名称…" />
            </div>
            <div class="aud-tabs">
                <div v-for="tab in audTabs" :key="tab.key" class="aud-tab" :class="{ active: audFilter === tab.key }" @click="audFilter = tab.key">{{ tab.label }}</div>
            </div>
            <div v-loading="audLoading" class="aud-list" @scroll="onAudScroll">
                <div v-for="a in filteredAuds" :key="a.id" class="aud-row" :class="{ sel: isAudSel(a) }" @click="toggleAud(a)">
                    <div class="aud-check" :class="{ on: isAudSel(a) }">{{ isAudSel(a) ? '✓' : '' }}</div>
                    <div class="aud-info">
                        <div class="aud-name">{{ a.name }}</div>
                        <div class="aud-meta">
                            <span class="src-tag" :class="a.type === 'import' ? 'src-import' : 'src-rule'">{{ a.type === 'import' ? '人群包导入' : '规则圈选' }}</span>
                            <span class="aud-cat">{{ a.cat || '未分类' }}</span>
                            · {{ fmtInt(a.num) }} 人
                            <span v-if="a.owner" class="aud-mine"> · 我创建</span>
                        </div>
                    </div>
                    <div class="aud-num">{{ fmtInt(a.num) }}<span>人</span></div>
                </div>
                <div v-if="!filteredAuds.length && !audLoading" class="aud-empty">没有符合条件的人群</div>
                <div v-if="audLoading" class="aud-empty">加载中…</div>
                <div v-if="audFinished && filteredAuds.length" class="aud-empty aud-end">已加载全部人群</div>
            </div>
            <div class="sel-summary">
                <div class="ss-num" v-loading="coverLoading">{{ fmtInt(selTotal) }}</div>
                <div class="ss-label">合计覆盖（去重）名客户</div>
                <button class="btn-line btn-sm" style="margin-left:auto" @click="goSeg">＋ 新建人群</button>
            </div>
        </section>

        <!-- 第 2 步：运营动作 -->
        <section class="section" id="section2">
            <div class="section-head"><span class="section-num">2</span>选择运营动作</div>
            <div class="section-tip">可多选运营动作（内容推送 / 营销活动 / 客户关怀）。「自动化运营」是独立旅程流程，与前面三个动作互斥。<b>一期仅开放「内容推送」；营销活动 / 客户关怀 / 自动化运营 为二期能力，标注「一期不做」的选项暂不可选。</b></div>
            <div class="opt-grid">
                <div v-for="a in ACTIONS" :key="a.key" class="opt-card" :class="{ sel: wiz.actions.includes(a.key), phase2: a.phase2 }" @click="toggleAction(a)">
                    <div class="opt-ico" :style="{ background: a.color }" v-html="a.icon"></div>
                    <div>
                        <div class="opt-name">{{ a.name }}<span v-if="a.phase2" class="phase-tag">一期不做</span></div>
                        <div class="opt-desc">{{ a.desc }}</div>
                    </div>
                    <div class="opt-check" :class="{ on: wiz.actions.includes(a.key) }">{{ wiz.actions.includes(a.key) ? '✓' : '' }}</div>
                </div>
            </div>
        </section>

        <!-- 第 3 步：动作内容 -->
        <section class="section" id="section3">
            <div class="section-head"><span class="section-num">3</span>配置动作内容</div>
            <div class="section-tip">根据上方选择的每个运营动作，分别填写对应的内容与素材。</div>
            <div v-if="!wiz.actions.length" class="cond-empty">请先在上方选择至少一个运营动作</div>
            <div v-for="k in wiz.actions" :key="k" class="act-block">
                <div class="act-block-head">
                    <span class="act-ico" :style="{ background: actColor(k) }" v-html="actIcon(k)"></span>
                    <span class="act-name">{{ actName(k) }}</span>
                </div>
                <label class="cfg-label">推送图片</label>
                <div class="up-box" @click="pickImage(k)">
                    <input type="file" :id="'upFile_' + k" accept="image/*" hidden @change="onImageChange(k, $event)" />
                    <div v-if="!actionImg[k]" class="up-empty">
                        <span class="up-plus">＋</span>
                        <span class="up-main">点击上传图片</span>
                        <span class="up-tip">支持 JPG / PNG，单张不超过 5MB，建议比例 1:1 或 4:3</span>
                    </div>
                    <div v-else class="up-preview">
                        <img class="up-thumb" :src="actionImg[k]" alt="已上传图片" />
                        <div class="up-meta">
                            <span class="up-ok">✓ 已上传</span>
                            <span class="up-fname">{{ actionName[k] }}</span>
                        </div>
                        <button type="button" class="up-del" @click.stop="clearImage(k)">移除</button>
                    </div>
                </div>
                <label class="cfg-label">推送文字</label>
                <textarea class="cfg-input textarea" v-model="actionBody[k]" placeholder="填写要推送的文字内容…"></textarea>
                <div class="cfg-note">发送顺序：<b>先发送图片，再发送文字</b>。仅上传图片或仅填写文字时，只发送已配置的那一项。</div>
            </div>
        </section>

        <!-- 第 4 步：触达配置 -->
        <section class="section" id="section4">
            <div class="section-head"><span class="section-num">4</span>触达配置</div>
            <div class="section-tip">配置运营动作的发送渠道、触发时机与触达频率。同一内容将通过所选渠道送达客户。<b>一期支持：渠道「企微 1v1」、触发时机「立即 / 定时」；标注「一期不做」的选项暂不可选。</b></div>

            <div class="sub-head">触达渠道</div>
            <div class="channel-row">
                <div v-for="c in CHANNELS" :key="c.key" class="channel-card" :class="{ sel: wiz.channels.includes(c.key), phase2: c.phase2 }" @click="toggleChannel(c)">
                    <div class="channel-name">{{ c.name }}<span v-if="c.phase2" class="phase-tag">一期不做</span></div>
                    <div class="channel-desc">{{ c.desc }}</div>
                    <div v-if="!c.phase2" class="channel-check" :class="{ on: wiz.channels.includes(c.key) }">{{ wiz.channels.includes(c.key) ? '✓' : '' }}</div>
                </div>
            </div>

            <div class="sub-head">触发时机</div>
            <div class="trig-grid">
                <div v-for="t in TRIGGERS" :key="t.key" class="trig-card" :class="{ sel: wiz.trigger === t.key, phase2: t.phase2 }" @click="toggleTrigger(t)">
                    <div class="trig-name">{{ t.name }}<span v-if="t.phase2" class="phase-tag">一期不做</span></div>
                    <div class="trig-desc">{{ t.desc }}</div>
                </div>
            </div>
            <div class="sel-summary trigger-detail">
                <template v-if="wiz.trigger === 'scheduled'">
                    <b>定时</b> · 设定日期时间，到点自动执行（单次）。
                    <span class="trig-cfg">
                        <span class="trig-cfg-label">执行日期</span>
                        <input type="date" class="cfg-input trig-input" v-model="wiz.schedDate" />
                        <span class="trig-cfg-label">执行时间</span>
                        <input type="time" class="cfg-input trig-input" v-model="wiz.schedTime" />
                    </span>
                </template>
                <template v-else>{{ triggerDesc }}</template>
            </div>

            <div class="sub-head">触达频率</div>
            <div class="freq-row">
                <div class="radio-row">
                    <label v-for="m in FREQ_MODES" :key="m" class="rdo" :class="{ sel: wiz.freqMode === m }" @click="wiz.freqMode = m">
                        <input type="radio" :checked="wiz.freqMode === m" />{{ m }}
                    </label>
                </div>
                <span v-if="wiz.freqMode !== '不限'" class="freq-num">
                    ≤ <input class="field-input" type="number" min="1" v-model.number="wiz.freqNum" /> 人 / <b>{{ wiz.freqMode === '每日触达' ? '天' : '小时' }}</b>
                </span>
            </div>
            <div class="hint">控制本任务的发送节奏，避免短时间集中打扰客户。例如「每日触达 500 人」表示按天匀速下发，客户不会被一次性轰炸。</div>

            <div class="gap-row">
                <span class="gap-label">发送间隔</span>
                <span class="gap-inputs">
                    <input class="field-input gap-in" type="number" min="1" max="3600" v-model.number="wiz.gapMin" />
                    <span class="gap-unit">秒</span>
                    <span class="gap-sep">～</span>
                    <input class="field-input gap-in" type="number" min="1" max="3600" v-model.number="wiz.gapMax" />
                    <span class="gap-unit">秒</span>
                </span>
                <span class="gap-note">到下一位客户的间隔，系统在区间内<b>随机取值</b>，避免固定节奏被判定为机器发送</span>
            </div>
            <div class="ge-box">
                <div class="ge-head">预估发送时长</div>
                <div v-if="!selTotal" class="ge-empty">请先在上方「1 选择目标人群」中勾选人群，选好后这里会自动估算发完这批客户需要多长时间。</div>
                <template v-else>
                    <div class="ge-grid">
                        <div class="ge-card">
                            <div class="ge-k">按中间间隔 <b>{{ fmtSec(gapMid) }}</b></div>
                            <div class="ge-v">约 {{ fmtDur(selTotal * gapMid) }}</div>
                            <div class="ge-s">{{ fmtInt(selTotal) }} 人 × {{ fmtSec(gapMid) }}</div>
                        </div>
                        <div class="ge-card">
                            <div class="ge-k">按最大间隔 <b>{{ fmtSec(gapMaxVal) }}</b></div>
                            <div class="ge-v">约 {{ fmtDur(selTotal * gapMaxVal) }}</div>
                            <div class="ge-s">{{ fmtInt(selTotal) }} 人 × {{ fmtSec(gapMaxVal) }}</div>
                        </div>
                    </div>
                    <div class="ge-note">发送总量 <b>{{ fmtInt(selTotal) }} 人</b>，间隔在 {{ fmtSec(gapMinVal) }} ～ {{ fmtSec(gapMaxVal) }} 之间随机取值，实际完成时间介于两个预估值之间。</div>
                </template>
            </div>

            <div class="cx-group">
                <div class="cx-title">客户体验保护</div>
                <div class="section-tip" style="margin:0 0 12px">为避免夜间打扰客户，二选一配置本任务的发送限制。</div>
                <div class="cx-modes">
                    <div class="cx-mode" :class="{ sel: wiz.cxMode === 'cutoff' }" @click="wiz.cxMode = 'cutoff'">
                        <div class="cx-mode-head"><span class="cx-radio"></span><span class="cx-mode-name">发送截止时间</span></div>
                        <div class="cx-mode-desc">到达设定时间后不再继续发送，<b>任务自动暂停</b>，剩余客户待恢复后从暂停处继续。</div>
                        <div v-if="wiz.cxMode === 'cutoff'" class="cx-mode-cfg">
                            每天 <input type="time" class="cfg-input cx-time" v-model="wiz.cxCutoff" /> 之后不再发送
                        </div>
                    </div>
                    <div class="cx-mode" :class="{ sel: wiz.cxMode === 'quiet' }" @click="wiz.cxMode = 'quiet'">
                        <div class="cx-mode-head"><span class="cx-radio"></span><span class="cx-mode-name">免打扰时段</span></div>
                        <div class="cx-mode-desc">时段内不打扰客户，发送队列保留不丢失，<b>时段结束后自动继续发送</b>。</div>
                        <div v-if="wiz.cxMode === 'quiet'" class="cx-mode-cfg">
                            <input type="time" class="cfg-input cx-time" v-model="wiz.cxFrom" /> ～ <span>{{ cxToDay }}</span> <input type="time" class="cfg-input cx-time" v-model="wiz.cxTo" />
                        </div>
                    </div>
                </div>
                <div class="cx-summary">
                    <template v-if="wiz.cxMode === 'cutoff'">
                        生效中：每天 <b>{{ wiz.cxCutoff || '21:00' }}</b> 之后本任务不再继续发送，任务自动暂停；剩余客户在任务恢复后从暂停处继续。
                    </template>
                    <template v-else>
                        生效中：每天 <b>{{ wiz.cxFrom || '22:00' }} ～ {{ cxToDay }} {{ wiz.cxTo || '08:00' }}</b> 不向客户发送，队列保留不丢失，时段结束后自动继续。
                    </template>
                </div>
            </div>
        </section>

        <!-- 第 5 步：确认创建 -->
        <section class="section" id="section5">
            <div class="section-head"><span class="section-num">5</span>确认并创建任务</div>
            <div class="section-tip">请核对整体配置，确认无误后创建。创建后任务将按触发时机进入执行。</div>
            <div class="task-name-field">
                <label class="field-label">任务名称</label>
                <input class="field-input" v-model="wiz.taskName" placeholder="例如：赛事完赛未购人群 · 7月复购唤醒" />
            </div>
            <div class="confirm-grid">
                <div class="confirm-card">
                    <div class="cc-label">运营动作</div>
                    <div class="cc-value">
                        <span v-for="k in wiz.actions" :key="k" class="act-chip confirm-chip">{{ actName(k) }}</span>
                        <span v-if="!wiz.actions.length">—</span>
                    </div>
                </div>
                <div class="confirm-card">
                    <div class="cc-label">触达渠道</div>
                    <div class="cc-value">{{ chNames.length ? chNames.join('、') : '—' }}</div>
                    <div class="cc-sub" v-if="chNames.length">共 {{ chNames.length }} 个渠道</div>
                </div>
                <div class="confirm-card">
                    <div class="cc-label">目标人群</div>
                    <div class="cc-value">
                        <span v-for="a in selectedAuds" :key="a.id" class="aud-pill">{{ a.name }}</span>
                        <span v-if="!selectedAuds.length">—</span>
                    </div>
                    <div class="cc-sub" v-if="selectedAuds.length">合计 {{ fmtInt(selTotal) }} 人</div>
                </div>
                <div class="confirm-card">
                    <div class="cc-label">触发时机</div>
                    <div class="cc-value">{{ trigText }}</div>
                </div>
            </div>
            <div class="flow">
                <div class="flow-node start">目标人群<div class="flow-sub">{{ selectedAuds.length ? selectedAuds.length + ' 个' : '(未选)' }}</div></div>
                <div class="flow-arrow">→</div>
                <div class="flow-node">运营动作<div class="flow-sub">{{ wiz.actions.length ? wiz.actions.map(actName).join('/') : '(未选)' }}</div></div>
                <div class="flow-arrow">→</div>
                <div class="flow-node">触达渠道<div class="flow-sub">{{ chNames.length ? chNames.join('/') : '(未选)' }}</div></div>
                <div class="flow-arrow">→</div>
                <div class="flow-node flow-end">转化<div class="flow-sub">等待执行</div></div>
            </div>
        </section>

        <!-- 底部操作栏 -->
        <div class="wizard-foot">
            <button class="btn-ghost" @click="saveDraft">保存草稿</button>
            <div class="grow"></div>
            <button class="btn-ghost" @click="goBack">取消</button>
            <button class="btn-primary" @click="createTask">创建任务</button>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { newgetcirclelist, newauth, newcrowdopscount, newcreatecrowdops, newsavecrowdopsdraft, newgetcrowdopsdetail, newuploadimg } from '@/assets/js/common.js'
import { encode, decode } from '@/assets/js/base64.js'

const router = useRouter()
const route = useRoute()

/* ============== 常量 ============== */
const STEPS = [
    { name: '目标人群', desc: '对谁' },
    { name: '运营动作', desc: '做什么' },
    { name: '动作内容', desc: '填什么' },
    { name: '触达配置', desc: '渠道·时机·频率' },
    { name: '确认创建', desc: '核对发布' }
]
// 一期：运营动作仅「内容推送」（默认选中且不可取消），触达渠道仅「企微 1v1」
const ACTIONS = [
    { key: 'push', name: '内容推送', color: '#0EA5E9', desc: '向人群发送商品卡片、调查问卷、通知公告等内容（不含玩法机制）。', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linejoin="round"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>' },
    { key: 'marketing', name: '营销活动', color: '#7C3AED', phase2: true, desc: '发起优惠券、拼团、抽奖等带玩法机制的营销活动。', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linejoin="round"><path d="M20 12v8H4v-8"/><rect x="2" y="7" width="20" height="5"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>' },
    { key: 'care', name: '客户关怀', color: '#10B981', phase2: true, desc: '新客欢迎、生日节日、赛事节点等情感触达。', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linejoin="round"><path d="M20.8 4.6a5.5 5.5 0 00-7.8 0L12 5.6l-1-1a5.5 5.5 0 00-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 000-7.8z"/></svg>' },
    { key: 'automation', name: '自动化运营', color: '#F59E0B', phase2: true, solo: true, desc: '配置用户旅程，按节点自动触发运营（独立流程，不可与前面三个动作同时选）。', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"/><path d="M20.5 15a9 9 0 11-2.1-9.4L23 10"/></svg>' }
]
const CHANNELS = [
    { key: 'wecom_1v1', name: '企微 1v1', desc: '使用设备单个发送（发送时间慢，大量发送有风险）' },
    { key: 'wecom_mass', name: '群发', phase2: true, desc: '通过官方渠道发送，速度快，更安全' },
    { key: 'wecom_moments', name: '朋友圈', phase2: true, desc: '企业微信朋友圈' },
    { key: 'miniapp_tpl', name: '小程序模版消息', phase2: true, desc: '微信小程序订阅消息 / 模板消息' }
]
const TRIGGERS = [
    { key: 'now', name: '立即', desc: '创建后马上执行一次。' },
    { key: 'scheduled', name: '定时', desc: '设定日期时间，到点自动执行（单次）。' },
    { key: 'behavior', name: '行为触发', phase2: true, desc: '监听客户行为事件，满足条件自动发送。' },
    { key: 'journey', name: '旅程节点', phase2: true, desc: '嵌入用户旅程，在指定节点自动触发。' }
]
const FREQ_MODES = ['不限', '每日触达', '每时触达']
const ACT_NAME_MAP = { push: '内容推送', marketing: '营销活动', care: '客户关怀', automation: '自动化运营' }
const ACT_COLOR_MAP = { push: '#0EA5E9', marketing: '#7C3AED', care: '#10B981', automation: '#F59E0B' }
const ACT_ICON_MAP = {}
ACTIONS.forEach(a => { ACT_ICON_MAP[a.key] = a.icon })
const CH_NAME_MAP = { wecom_1v1: '企微 1v1', wecom_mass: '群发', wecom_moments: '朋友圈', miniapp_tpl: '小程序模版消息' }

const actName = (k) => ACT_NAME_MAP[k] || k
const actColor = (k) => ACT_COLOR_MAP[k] || '#94A3B8'
const actIcon = (k) => ACT_ICON_MAP[k] || ''

/* ============== 目标人群（真实接口：/api/audience/list，滑动翻页加载） ============== */
const audiences = ref([])
const audLoading = ref(false)
const audFinished = ref(false)
const audPage = ref(1)
const audPageSize = 20
const audSearch = ref('')
const audFilter = ref('all')
const selectedAuds = ref([])   // [{id, name, cat, num, type, owner}]
const curUserName = ref('')

// 分类 Tab：全部 + 数据中的分类 + 我创建的
const audTabs = computed(() => {
    const cats = ['全部']
    audiences.value.forEach(a => {
        const c = a.cat || '未分类'
        if (cats.indexOf(c) < 0) cats.push(c)
    })
    const tabs = cats.map(c => ({ key: c === '全部' ? 'all' : c, label: c }))
    tabs.push({ key: 'mine', label: '我创建的' })
    return tabs
})

// 加载客户人群列表（分页累加，触底加载更多；切筛选/搜索时重置）
const loadAudiences = (reset = false) => {
    if (reset) {
        audPage.value = 1
        audFinished.value = false
        audiences.value = []
    }
    if (audLoading.value || audFinished.value) return
    audLoading.value = true
    newgetcirclelist({ page: audPage.value, page_size: audPageSize }).then((response) => {
        audLoading.value = false
        if (response.data.msg.code == 0) {
            const d = response.data.data || {}
            const list = (d.list || (Array.isArray(d) ? d : []))
            const mapped = list.map(i => {
                const cat = i.category || i.cat || ''
                const num = i.member_count || i.count || i.customer_count || 0
                return {
                    id: i.id,
                    name: i.name || i.title || '',
                    cat,
                    num,
                    type: (i.is_import === '1' || i.is_import === 1 || i.is_import === true) ? 'import' : 'rule',
                    owner: curUserName.value && i.created_by_name === curUserName.value
                }
            }).filter(a => a.name)
            audiences.value = audiences.value.concat(mapped)
            // 翻页终止条件：当前页不足 page_size 即到底
            if (list.length < audPageSize) audFinished.value = true
            audPage.value += 1
        } else {
            audFinished.value = true
        }
    }).catch(() => { audLoading.value = false; audFinished.value = true })
}

// 人群列表触底加载更多
const onAudScroll = (e) => {
    const el = e.target
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
        loadAudiences()
    }
}

// 当前登录用户（用于「我创建的」筛选）
const loadMe = () => {
    newauth().then((response) => {
        if (response.data.msg.code == 0 && response.data.data) {
            curUserName.value = response.data.data.username || ''
            loadAudiences()
        }
    })
}

// 筛选人群
const filteredAuds = computed(() => {
    const kw = (audSearch.value || '').trim().toLowerCase()
    return audiences.value.filter(a => {
        if (audFilter.value === 'mine' && !a.owner) return false
        if (audFilter.value !== 'all' && audFilter.value !== 'mine' && (a.cat || '未分类') !== audFilter.value) return false
        if (kw && a.name.toLowerCase().indexOf(kw) < 0) return false
        return true
    })
})
const isAudSel = (a) => selectedAuds.value.some(x => x.id === a.id)
const toggleAud = (a) => {
    const idx = selectedAuds.value.findIndex(x => x.id === a.id)
    if (idx >= 0) selectedAuds.value.splice(idx, 1)
    else selectedAuds.value.push({ id: a.id, name: a.name, num: a.num, cat: a.cat, type: a.type })
    calcCoverCount()
}

// 覆盖人数（单独接口实时算，多人群并集去重）
const selTotal = ref(0)
const coverLoading = ref(false)
const calcCoverCount = () => {
    const ids = selectedAuds.value.map(a => a.id)
    if (!ids.length) { selTotal.value = 0; return }
    coverLoading.value = true
    newcrowdopscount({ audience_ids: ids }).then((response) => {
        coverLoading.value = false
        if (response.data.msg.code == 0) {
            selTotal.value = (response.data.data && response.data.data.count) || 0
        }
    }).catch(() => { coverLoading.value = false })
}

/* ============== 向导状态 ============== */
// 一期：运营动作默认「内容推送」，触达渠道默认「企微 1v1」，触发时机默认「立即」
const wiz = reactive({
    actions: ['push'],
    channels: ['wecom_1v1'],
    trigger: 'now',
    schedDate: todayStr(),
    schedTime: '09:00',
    freqMode: '不限',
    freqNum: 500,
    gapMin: 3,
    gapMax: 8,
    cxMode: 'quiet',
    cxCutoff: '21:00',
    cxFrom: '22:00',
    cxTo: '08:00',
    taskName: ''
})
const actionImg = reactive({})
const actionName = reactive({})
const actionBody = reactive({})

/* ============== 编辑态：列表「编辑」带 id 进入，回填草稿 ============== */
const editingId = ref(Number(route.query.id || 0))
const isEditing = computed(() => editingId.value > 0)
const fmtHm = (v) => {
    const s = String(v || '')
    return /^\d{2}:\d{2}/.test(s) ? s.slice(0, 5) : s
}
const safeDecode = (s) => {
    try { return decode(s || '') } catch (e) { return '' }
}
const loadDetail = (id) => {
    newgetcrowdopsdetail({ id }).then((response) => {
        if (response.data.msg.code != 0) {
            ElMessage.error(response.data.msg.info || '任务加载失败')
            return
        }
        const d = response.data.data || {}
        wiz.taskName = d.name || ''
        wiz.trigger = d.trigger_type || 'now'
        const execAt = String(d.execute_at || '')
        if (execAt) {
            wiz.schedDate = execAt.slice(0, 10)
            wiz.schedTime = fmtHm(execAt.slice(11, 16)) || '09:00'
        }
        if (d.cx_mode) wiz.cxMode = d.cx_mode
        if (d.cx_cutoff) wiz.cxCutoff = fmtHm(d.cx_cutoff)
        if (d.cx_from) wiz.cxFrom = fmtHm(d.cx_from)
        if (d.cx_to) wiz.cxTo = fmtHm(d.cx_to)
        // 频率/间隔只存在群发任务上；草稿无群发任务时保留页面默认值
        if (d.bulk_task_id > 0) {
            wiz.freqMode = d.freq_mode || '不限'
            if (d.freq_num > 0) wiz.freqNum = d.freq_num
            if (d.gap_min > 0) wiz.gapMin = d.gap_min
            if (d.gap_max > 0) wiz.gapMax = d.gap_max
        }
        const ids = d.audience_ids || []
        const names = d.audience_names || []
        selectedAuds.value = ids.map((aid, i) => ({ id: aid, name: names[i] || ('人群 ' + aid), num: 0, cat: '', type: 'rule' }))
        selTotal.value = d.cover_count || 0
        ;(d.content || []).forEach((it) => {
            if (it.msg_type === 'Picture') {
                actionImg.push = it.thumbUrl || safeDecode(it.content)
                actionName.push = '已上传图片'
            }
            if (it.msg_type === 'Text') {
                actionBody.push = safeDecode(it.content)
            }
        })
    }).catch(() => { ElMessage.error('任务加载失败') })
}

/* ============== 运营动作（一期仅内容推送，默认选中不可取消） ============== */
const toggleAction = (a) => {
    if (a.phase2) return
    if (a.key === 'push') return // 一期仅内容推送，不可取消
    const idx = wiz.actions.indexOf(a.key)
    if (idx >= 0) wiz.actions.splice(idx, 1)
    else if (a.solo) wiz.actions = [a.key]
    else { wiz.actions = wiz.actions.filter(k => { const x = ACTIONS.find(y => y.key === k); return !(x && x.solo) }); wiz.actions.push(a.key) }
}

/* ============== 动作内容（图片上传） ============== */
const pickImage = (k) => { document.getElementById('upFile_' + k) && document.getElementById('upFile_' + k).click() }
const onImageChange = (k, e) => {
    const file = e.target.files && e.target.files[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) { ElMessage.warning('图片不能超过 5MB'); return }
    newuploadimg(file).then((response) => {
        if (response.data.msg.code == 0 && response.data.data && response.data.data.url) {
            actionImg[k] = response.data.data.url
            actionName[k] = file.name
        } else {
            ElMessage.error('图片上传失败')
        }
    }).catch(() => { ElMessage.error('图片上传失败') })
    e.target.value = ''
}
const clearImage = (k) => { delete actionImg[k]; delete actionName[k] }

/* ============== 触达配置（一期仅企微 1v1，默认选中不可取消） ============== */
const toggleChannel = (c) => {
    if (c.phase2) return
    if (c.key === 'wecom_1v1') return // 一期仅企微 1v1，不可取消
    const idx = wiz.channels.indexOf(c.key)
    if (idx >= 0) wiz.channels.splice(idx, 1)
    else wiz.channels.push(c.key)
}
const toggleTrigger = (t) => { if (t.phase2) return; wiz.trigger = t.key }
const triggerDesc = computed(() => {
    const t = TRIGGERS.find(x => x.key === wiz.trigger)
    return t ? t.name + ' · ' + t.desc : '请选择触发时机'
})
const chNames = computed(() => wiz.channels.map(k => CH_NAME_MAP[k] || k))
const trigText = computed(() => {
    if (!wiz.trigger) return '—'
    if (wiz.trigger === 'scheduled') return '定时 · ' + (wiz.schedDate || todayStr()) + ' ' + (wiz.schedTime || '09:00')
    const t = TRIGGERS.find(x => x.key === wiz.trigger)
    return t ? t.name : wiz.trigger
})
const cxToDay = computed(() => (wiz.cxFrom || '22:00') > (wiz.cxTo || '08:00') ? '次日' : '')

/* ============== 发送间隔预估 ============== */
const gapMinVal = computed(() => Math.min(wiz.gapMin || 0, wiz.gapMax || 0))
const gapMaxVal = computed(() => Math.max(wiz.gapMin || 0, wiz.gapMax || 0))
const gapMid = computed(() => (gapMinVal.value + gapMaxVal.value) / 2)
const fmtSec = (v) => { const r = Math.round(v * 10) / 10; return (r % 1 === 0 ? r.toFixed(0) : r.toFixed(1)) + ' 秒' }
const fmtDur = (sec) => {
    sec = Math.max(0, Math.round(sec))
    const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60
    if (d > 0) return d + ' 天 ' + h + ' 小时'
    if (h > 0) return h + ' 小时 ' + m + ' 分'
    if (m > 0) return m + ' 分 ' + s + ' 秒'
    return s + ' 秒'
}
const fmtInt = (v) => Math.round(v || 0).toLocaleString('en-US')

/* ============== 校验 ============== */
const issues = computed(() => {
    const arr = []
    if (!selectedAuds.value.length) arr.push('选择目标人群')
    if (!wiz.actions.length) arr.push('选择运营动作')
    if (!wiz.channels.length) arr.push('配置触达渠道')
    if (!wiz.trigger) arr.push('选择触发时机')
    return arr
})

/* ============== 操作 ============== */
const goBack = () => router.back()
const goSeg = () => router.push({ path: '/navkh/circlepeopleedit' })
// 保存草稿：允许配置不完整，后端落 status=0 草稿，列表「草稿」筛选中可见
const saveDraft = () => {
    newsavecrowdopsdraft(buildPayload()).then((response) => {
        if (response.data.msg.code == 0) {
            ElMessage.success('草稿已保存')
            router.push({ path: '/navkh/crowdops' })
        } else {
            ElMessage.error(response.data.msg.info || '保存失败')
        }
    }).catch(() => { ElMessage.error('保存失败，请稍后重试') })
}

// 组装创建/草稿共用的提交参数
const buildPayload = () => {
    const payload = {
        name: (wiz.taskName || '').trim(),
        action_type: 'push',
        send_channel: 'wecom_1v1',
        audience_ids: selectedAuds.value.map(a => a.id),
        content: buildContent(),
        trigger_type: wiz.trigger,
        freq_mode: wiz.freqMode === '不限' ? '' : wiz.freqMode,
        freq_num: wiz.freqMode === '不限' ? 0 : (wiz.freqNum || 0),
        gap_min: wiz.gapMin || 0,
        gap_max: wiz.gapMax || 0,
        cx_mode: wiz.cxMode,
        cx_cutoff: wiz.cxMode === 'cutoff' ? (wiz.cxCutoff || '') : '',
        cx_from: wiz.cxMode === 'quiet' ? (wiz.cxFrom || '') : '',
        cx_to: wiz.cxMode === 'quiet' ? (wiz.cxTo || '') : ''
    }
    if (wiz.trigger === 'scheduled') {
        payload.execute_at = wiz.schedDate + ' ' + wiz.schedTime + ':00'
    }
    // 编辑态：草稿按原 id 覆盖保存，不新增记录
    if (editingId.value > 0) payload.id = editingId.value
    return payload
}

// 组装群发 content（与群发接口同构 JSON 数组：先图片后文字；content 字段 base64 编码）
const buildContent = () => {
    const list = []
    const push = wiz.actions[0] || 'push'
    if (actionImg[push]) {
        list.push({ msg_type: 'Picture', content: encode(actionImg[push]), desc: '', thumbUrl: actionImg[push] })
    }
    const body = (actionBody[push] || '').trim()
    if (body) {
        list.push({ msg_type: 'Text', content: encode(body) })
    }
    return list
}

const createTask = () => {
    if (issues.value.length) { ElMessage.warning('请先完成：' + issues.value.join('、')); return }
    if (!(wiz.taskName || '').trim()) { ElMessage.warning('请填写任务名称'); return }

    const payload = buildPayload()
    if (!payload.content.length) { ElMessage.warning('请填写推送内容（图片或文字）'); return }
    // 编辑草稿后创建：草稿转正而不是新增一条任务
    if (editingId.value > 0) payload.draft_id = editingId.value

    newcreatecrowdops(payload).then((response) => {
        if (response.data.msg.code == 0) {
            ElMessage.success('任务创建成功')
            router.push({ path: '/navkh/crowdops' })
        } else {
            ElMessage.error(response.data.msg.info || '创建失败')
        }
    }).catch(() => { ElMessage.error('创建失败，请稍后重试') })
}

function todayStr() {
    const d = new Date(), p = n => (n < 10 ? '0' : '') + n
    return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate())
}

onMounted(() => {
    if (editingId.value > 0) loadDetail(editingId.value)
    loadMe()
})
</script>

<style scoped>
.wiz-page {
    --bg: #F7F8FC;
    --card: #ffffff;
    --primary: #4F46E5;
    --primary2: #7C3AED;
    --primarySoft: #A5B4FC;
    --red: #EF4444;
    --green: #10B981;
    --amber: #F59E0B;
    --text: #1E293B;
    --muted: #64748B;
    --grid: #EEF1F6;
    --border: #E8ECF3;
    --shadow: 0 1px 3px rgba(15, 23, 42, .06), 0 10px 30px rgba(79, 70, 229, .06);
    --radius: 14px;
}
.grow { flex: 1; }
.crumb { font-size: 13px; color: var(--muted); margin-bottom: 10px; }
.crumb b { color: var(--text); }

/* 步骤条 */
.stepper { display: flex; align-items: center; background: var(--card); border-radius: var(--radius); box-shadow: var(--shadow); padding: 14px 18px; margin-bottom: 18px; position: sticky; top: 0px; z-index: 20; }
.step { display: flex; align-items: center; flex: 1; position: relative; text-decoration: none; }
.step:last-child { flex: none; }
.step-line { flex: 1; height: 3px; background: var(--grid); margin: 0 10px; border-radius: 3px; }
.step-dot { width: 28px; height: 28px; border-radius: 50%; display: grid; place-items: center; font-weight: 800; font-size: 12px; background: var(--grid); color: var(--muted); flex-shrink: 0; }
.step-meta { margin-left: 8px; }
.step-name { font-size: 12px; font-weight: 700; color: var(--text); white-space: nowrap; }
.step-desc { font-size: 10.5px; color: var(--muted); margin-top: 1px; white-space: nowrap; }

/* 校验提示 */
.val-summary { background: #FFF7ED; border: 1px solid rgba(245, 158, 11, .35); border-radius: 12px; padding: 14px 18px; margin-bottom: 18px; display: none; }
.val-summary.active { display: block; }
.val-title { font-weight: 800; color: #B45309; font-size: 13px; margin-bottom: 6px; }
.val-list { margin: 0; padding-left: 18px; color: #92400E; font-size: 12.5px; line-height: 1.8; }

/* 区块 */
.section { background: var(--card); border-radius: var(--radius); box-shadow: var(--shadow); padding: 22px; margin-bottom: 18px; scroll-margin-top: 130px; }
.section-head { font-size: 16px; font-weight: 800; margin-bottom: 4px; display: flex; align-items: center; gap: 10px; }
.section-num { width: 28px; height: 28px; border-radius: 50%; display: grid; place-items: center; font-weight: 800; font-size: 13px; background: linear-gradient(135deg, var(--primary), var(--primary2)); color: #fff; flex-shrink: 0; }
.section-tip { font-size: 12.5px; color: var(--muted); margin-bottom: 18px; }
.sub-head { font-size: 14px; font-weight: 800; margin: 22px 0 12px; padding-left: 11px; border-left: 3px solid var(--primary); }
.sub-head:first-of-type { margin-top: 4px; }
.cond-empty { text-align: center; color: var(--muted); font-size: 13px; padding: 26px 0; }

/* 按钮 */
.btn-primary { background: linear-gradient(90deg, var(--primary), var(--primary2)); color: #fff; border: 0; border-radius: 10px; padding: 9px 18px; font-weight: 700; cursor: pointer; font-size: 13px; }
.btn-primary:hover { opacity: .92; }
.btn-ghost { background: #F1F3F9; color: var(--text); border: 0; border-radius: 10px; padding: 9px 18px; font-weight: 600; cursor: pointer; font-size: 13px; }
.btn-ghost:hover { background: #E7EAF2; }
.btn-line { background: #fff; color: var(--primary); border: 1px solid var(--border); border-radius: 10px; padding: 9px 18px; font-weight: 600; cursor: pointer; font-size: 13px; }
.btn-line:hover { background: #F8FAFC; }
.btn-sm { padding: 6px 12px; font-size: 12.5px; border-radius: 8px; }

/* 表单 */
.field-label { font-size: 12.5px; font-weight: 700; color: var(--text); margin-bottom: 7px; display: block; }
.field-input, .cfg-input { width: 100%; padding: 9px 12px; border: 1px solid var(--border); border-radius: 10px; font-size: 13.5px; color: var(--text); background: #fff; font-family: inherit; }
.field-input:focus, .cfg-input:focus { outline: none; border-color: var(--primary); }
.textarea { resize: vertical; min-height: 64px; }
.cfg-label { display: block; font-size: 12.5px; color: var(--muted); font-weight: 600; margin: 14px 0 6px; }
.cfg-note { font-size: 12px; color: var(--muted); margin-top: 8px; line-height: 1.7; }
.hint { font-size: 11.5px; color: var(--muted); margin-top: 6px; }

/* 目标人群 */
.sel-bar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding: 12px 16px; background: rgba(79, 70, 229, .05); border: 1px solid var(--border); border-radius: 12px; margin-bottom: 14px; }
.sel-bar-label { font-size: 13px; font-weight: 700; color: var(--text); white-space: nowrap; }
.sel-bar-label b { color: var(--primary); }
.chips { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.chip { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: 600; padding: 5px 10px; border-radius: 999px; background: #fff; border: 1px solid var(--border); color: var(--text); }
.chip-x { cursor: pointer; color: var(--muted); font-weight: 800; line-height: 1; padding: 0 2px; }
.chip-x:hover { color: var(--red); }
.chips-empty { font-size: 12.5px; color: var(--muted); }
.aud-toolbar { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.aud-search { max-width: 320px; }
.aud-tabs { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; border-bottom: 1px solid var(--border); padding-bottom: 10px; }
.aud-tab { padding: 7px 14px; border-radius: 999px; font-size: 13px; font-weight: 700; color: var(--muted); cursor: pointer; border: 1px solid transparent; }
.aud-tab:hover { background: #F1F3F9; color: var(--text); }
.aud-tab.active { background: linear-gradient(90deg, var(--primary), var(--primary2)); color: #fff; }
.aud-list { border: 1px solid var(--border); border-radius: 12px; overflow: hidden; max-height: 340px; overflow-y: auto; min-height: 80px; }
.aud-row { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-bottom: 1px solid var(--grid); cursor: pointer; transition: .12s; }
.aud-row:last-child { border-bottom: 0; }
.aud-row:hover { background: #FAFBFE; }
.aud-row.sel { background: rgba(79, 70, 229, .05); }
.aud-check { width: 20px; height: 20px; border-radius: 6px; border: 1.5px solid var(--border); display: grid; place-items: center; color: #fff; font-size: 12px; flex-shrink: 0; }
.aud-check.on { background: var(--primary); border-color: var(--primary); }
.aud-info { flex: 1; min-width: 0; }
.aud-name { font-weight: 700; font-size: 14px; }
.aud-meta { font-size: 12px; color: var(--muted); margin-top: 3px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.aud-mine { color: var(--green); font-weight: 700; }
.src-tag { display: inline-flex; align-items: center; font-size: 11.5px; font-weight: 700; padding: 2px 8px; border-radius: 6px; white-space: nowrap; }
.src-rule { color: #4F46E5; background: rgba(79, 70, 229, .10); }
.src-import { color: #0284C7; background: rgba(14, 165, 233, .13); }
.aud-num { font-size: 16px; font-weight: 800; color: var(--primary); white-space: nowrap; }
.aud-num span { font-size: 12px; color: var(--muted); font-weight: 600; }
.aud-empty { padding: 28px; text-align: center; color: var(--muted); font-size: 13px; }
.sel-summary { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; margin-top: 14px; padding: 12px 16px; background: rgba(79, 70, 229, .06); border: 1px dashed rgba(79, 70, 229, .3); border-radius: 12px; }
.ss-num { font-size: 24px; font-weight: 800; color: var(--primary); }
.ss-label { font-size: 12.5px; color: var(--muted); }
.trigger-detail { border-style: solid; background: #FAFBFE; border-color: var(--border); font-size: 13px; color: var(--text); }
.trig-cfg { display: inline-flex; align-items: center; gap: 8px; margin-left: 14px; flex-wrap: wrap; }
.trig-cfg-label { font-size: 12.5px; color: var(--muted); }
.trig-input { width: 130px; flex: 0 0 auto; padding: 7px 10px; }

/* 运营动作 */
.opt-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.opt-card { border: 1.5px solid var(--border); border-radius: 14px; padding: 16px; cursor: pointer; transition: .15s; display: flex; gap: 12px; align-items: flex-start; }
.opt-card:hover { border-color: var(--primarySoft); }
.opt-card.sel { border-color: var(--primary); background: rgba(79, 70, 229, .05); }
.opt-card.phase2 { background: #FAFBFE; cursor: not-allowed; }
.opt-card.phase2:hover { border-color: var(--border); }
.opt-card.phase2 .opt-ico { filter: grayscale(1); opacity: .55; }
.opt-card.phase2 .opt-name { color: var(--muted); }
.opt-ico { width: 42px; height: 42px; border-radius: 12px; display: grid; place-items: center; flex-shrink: 0; color: #fff; }
.opt-ico :deep(svg) { display: block; }
.opt-name { font-size: 15px; font-weight: 800; }
.opt-desc { font-size: 12px; color: var(--muted); margin-top: 3px; line-height: 1.5; }
.opt-check { margin-left: auto; width: 20px; height: 20px; border-radius: 6px; border: 1.5px solid var(--border); display: grid; place-items: center; color: #fff; font-size: 12px; flex-shrink: 0; }
.opt-check.on { background: var(--primary); border-color: var(--primary); }
.phase-tag { display: inline-block; margin-left: 8px; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 700; background: #F3F4F6; color: #6B7280; border: 1px solid #E5E7EB; vertical-align: middle; }

/* 动作内容 */
.act-block { border: 1px solid var(--border); border-radius: 14px; padding: 18px; margin-bottom: 14px; }
.act-block-head { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
.act-ico { width: 30px; height: 30px; border-radius: 8px; display: grid; place-items: center; color: #fff; }
.act-ico :deep(svg) { width: 16px; height: 16px; display: block; }
.act-name { font-size: 14.5px; font-weight: 800; }
.up-box { border: 1.5px dashed var(--border); border-radius: 12px; padding: 22px 16px; text-align: center; background: #FAFBFE; cursor: pointer; }
.up-box:hover { border-color: var(--primary); background: #F5F7FF; }
.up-empty .up-plus { font-size: 26px; font-weight: 800; color: var(--primary); display: block; }
.up-empty .up-main { font-size: 13.5px; font-weight: 700; color: var(--text); display: block; margin-top: 4px; }
.up-empty .up-tip { font-size: 12px; color: var(--muted); display: block; margin-top: 6px; }
.up-preview { display: flex; align-items: center; gap: 12px; }
.up-thumb { width: 72px; height: 72px; border-radius: 8px; object-fit: cover; flex-shrink: 0; }
.up-meta { flex: 1; text-align: left; }
.up-ok { color: var(--green); font-weight: 700; font-size: 13px; }
.up-fname { color: var(--muted); font-size: 12px; margin-left: 8px; }
.up-del { border: 1px solid var(--border); background: #fff; color: var(--red); border-radius: 8px; padding: 5px 12px; font-size: 12.5px; cursor: pointer; }

/* 触达配置 */
.channel-row { display: flex; gap: 12px; flex-wrap: wrap; }
.channel-card { flex: 1; min-width: 180px; border: 1.5px solid var(--border); border-radius: 14px; padding: 14px 16px; cursor: pointer; transition: .15s; position: relative; }
.channel-card:hover { border-color: var(--primarySoft); }
.channel-card.sel { border-color: var(--primary); background: rgba(79, 70, 229, .05); }
.channel-card.phase2 { background: #FAFBFE; cursor: not-allowed; }
.channel-card.phase2 .channel-name { color: var(--muted); }
.channel-name { font-size: 14px; font-weight: 800; }
.channel-desc { font-size: 12px; color: var(--muted); margin-top: 4px; line-height: 1.5; }
.channel-check { position: absolute; top: 12px; right: 12px; width: 20px; height: 20px; border-radius: 6px; border: 1.5px solid var(--border); display: grid; place-items: center; color: #fff; font-size: 12px; }
.channel-check.on { background: var(--primary); border-color: var(--primary); }
.trig-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
.trig-card { border: 1.5px solid var(--border); border-radius: 12px; padding: 14px; cursor: pointer; transition: .15s; }
.trig-card:hover { border-color: var(--primarySoft); }
.trig-card.sel { border-color: var(--primary); background: rgba(79, 70, 229, .05); }
.trig-card.phase2 { background: #FAFBFE; cursor: not-allowed; }
.trig-card.phase2 .trig-name { color: var(--muted); }
.trig-name { font-size: 13.5px; font-weight: 800; }
.trig-desc { font-size: 11.5px; color: var(--muted); margin-top: 4px; line-height: 1.5; }

/* 频率 / 间隔 */
.freq-row { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.radio-row { display: flex; gap: 10px; flex-wrap: wrap; }
.rdo { display: flex; align-items: center; gap: 7px; padding: 8px 14px; border: 1px solid var(--border); border-radius: 10px; cursor: pointer; font-size: 13px; font-weight: 600; }
.rdo.sel { border-color: var(--primary); background: rgba(79, 70, 229, .06); color: var(--primary); }
.rdo input { display: none; }
.freq-num { font-size: 13px; font-weight: 700; color: var(--text); display: inline-flex; align-items: center; gap: 8px; }
.freq-num input { width: 96px; }
.gap-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 16px; padding: 12px 14px; background: #fff; border: 1px solid var(--border); border-radius: 12px; }
.gap-label { font-size: 13px; font-weight: 800; color: var(--text); }
.gap-inputs { display: inline-flex; align-items: center; gap: 8px; }
.gap-in { width: 74px; text-align: center; padding: 8px 10px; font-weight: 700; }
.gap-unit { font-size: 13px; font-weight: 600; color: var(--muted); }
.gap-sep { font-size: 13px; color: var(--muted); }
.gap-note { font-size: 12px; color: var(--muted); }
.gap-note b { color: var(--text); }
.ge-box { margin-top: 14px; padding: 14px 16px; border-radius: 12px; background: #FAFBFE; border: 1px solid var(--border); }
.ge-head { font-size: 12.5px; font-weight: 800; color: var(--text); margin-bottom: 10px; }
.ge-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.ge-card { padding: 12px 14px; border-radius: 10px; background: #fff; border: 1px solid var(--border); }
.ge-k { font-size: 12px; font-weight: 600; color: var(--muted); }
.ge-k b { color: var(--text); font-weight: 800; }
.ge-v { font-size: 17px; font-weight: 900; color: var(--primary); margin: 5px 0 4px; }
.ge-s { font-size: 11.5px; color: var(--muted); }
.ge-note { font-size: 11.5px; color: var(--muted); margin-top: 10px; line-height: 1.7; }
.ge-note b { color: var(--text); }
.ge-empty { font-size: 12.5px; color: var(--muted); line-height: 1.7; }

/* 客户体验保护 */
.cx-group { margin-top: 24px; padding: 18px; background: #FAFBFE; border: 1px solid var(--border); border-radius: 14px; border-left: 4px solid var(--green); }
.cx-title { font-size: 15px; font-weight: 800; color: var(--text); margin-bottom: 6px; display: flex; align-items: center; gap: 8px; }
.cx-title::before { content: ""; width: 7px; height: 7px; border-radius: 50%; background: var(--green); }
.cx-modes { display: flex; flex-direction: column; gap: 10px; }
.cx-mode { padding: 13px 15px; background: #fff; border: 1.5px solid var(--border); border-radius: 12px; cursor: pointer; transition: .15s; }
.cx-mode:hover { border-color: var(--primarySoft); }
.cx-mode.sel { border-color: var(--primary); background: rgba(79, 70, 229, .04); }
.cx-mode-head { display: flex; align-items: center; gap: 9px; }
.cx-radio { width: 16px; height: 16px; border-radius: 50%; border: 1.5px solid var(--border); flex-shrink: 0; position: relative; background: #fff; }
.cx-mode.sel .cx-radio { border-color: var(--primary); }
.cx-mode.sel .cx-radio::after { content: ""; position: absolute; inset: 4px; border-radius: 50%; background: var(--primary); }
.cx-mode-name { font-size: 13.5px; font-weight: 800; color: var(--text); }
.cx-mode-desc { font-size: 12px; color: var(--muted); margin: 5px 0 0 25px; line-height: 1.6; }
.cx-mode-cfg { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin: 11px 0 0 25px; font-size: 13px; font-weight: 600; color: var(--text); }
.cx-time { width: 118px; flex: 0 0 auto; padding: 7px 10px; font-weight: 700; text-align: center; }
.cx-summary { margin-top: 12px; padding: 10px 13px; border-radius: 10px; background: #fff; border: 1px solid var(--border); font-size: 12px; color: var(--muted); line-height: 1.7; }
.cx-summary b { color: var(--text); }

/* 确认创建 */
.task-name-field { margin-bottom: 16px; }
.task-name-field .field-input { max-width: 560px; }
.confirm-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 18px; }
.confirm-card { background: #FAFBFE; border: 1px solid var(--border); border-radius: 12px; padding: 16px; }
.cc-label { font-size: 12px; color: var(--muted); font-weight: 600; }
.cc-value { font-size: 15px; font-weight: 800; margin-top: 5px; }
.cc-sub { font-size: 12px; color: var(--muted); margin-top: 3px; }
.confirm-chip { display: inline-block; background: var(--primarySoft); color: var(--primary); font-size: 12px; font-weight: 700; padding: 2px 9px; border-radius: 999px; margin: 2px 4px 2px 0; }
.aud-pill { display: inline-block; background: rgba(79, 70, 229, .10); color: var(--primary); font-size: 12px; font-weight: 700; padding: 2px 9px; border-radius: 999px; margin: 2px 4px 2px 0; }
.flow { display: flex; align-items: center; flex-wrap: wrap; padding: 18px; background: #FAFBFE; border: 1px solid var(--border); border-radius: 12px; margin: 14px 0 0; }
.flow-node { padding: 9px 16px; border-radius: 10px; font-weight: 700; font-size: 13px; background: #fff; border: 1px solid var(--border); text-align: center; }
.flow-node.start { background: linear-gradient(135deg, var(--primary), var(--primary2)); color: #fff; border: 0; }
.flow-node .flow-sub { font-weight: 500; font-size: 11.5px; margin-top: 3px; color: var(--muted); }
.flow-node.start .flow-sub { color: rgba(255, 255, 255, .85); }
.flow-arrow { color: var(--muted); font-size: 18px; padding: 0 10px; }

/* 底部操作栏 */
.wizard-foot { display: flex; align-items: center; gap: 12px; padding: 16px 22px; background: var(--card); border-radius: var(--radius); box-shadow: var(--shadow); position: sticky; bottom: 0px; z-index: 25; border: 1px solid var(--border); }

@media (max-width: 1180px) {
    .confirm-grid, .opt-grid { grid-template-columns: repeat(2, 1fr); }
    .trig-grid { grid-template-columns: repeat(2, 1fr); }
}
</style>
